import React, { useState, useEffect, useCallback } from 'react';
import {
  GripVertical, Eye, EyeOff, Layout, Layers, Plus, Trash2,
  Save, Loader2, ChevronDown, ChevronRight, RotateCcw,
  Home, Info, Package, ListOrdered, CheckCircle, BarChart2,
  Clock, Image, Quote, HelpCircle, Users, Building2, MapPin,
  DollarSign, FileText, Mail, Briefcase, GraduationCap, Wrench, Trophy
} from 'lucide-react';
import api from '../../../services/api';

// Icon map for sections
const SECTION_ICONS = {
  home: Home, about: Info, services: Package, how_it_works: ListOrdered,
  why_choose_us: CheckCircle, stats: BarChart2, legacy: Clock, gallery: Image,
  testimonials: Quote, faq: HelpCircle, team: Users, clients: Building2,
  locations: MapPin, pricing: DollarSign, blogs: FileText, inbox: Mail,
  profile: Users, education: GraduationCap, career: Briefcase, skills: Wrench, awards: Trophy,
};

// Available layouts per section type
const SECTION_LAYOUTS = {
  home: [
    { key: 'hero', label: 'Hero Banner', description: 'Full-width hero with background' },
    { key: 'split', label: 'Split Screen', description: 'Content and image side by side' },
    { key: 'minimal', label: 'Minimal', description: 'Clean, centered content' },
  ],
  about: [
    { key: 'default', label: 'Default', description: 'Standard about section' },
    { key: 'hero', label: 'Hero Style', description: 'Large image with overlay text' },
    { key: 'story', label: 'Story', description: 'Timeline narrative style' },
    { key: 'classic', label: 'Classic', description: 'Traditional layout with sidebar' },
  ],
  services: [
    { key: 'grid', label: 'Grid', description: 'Cards in a grid layout' },
    { key: 'list', label: 'List', description: 'Vertical list with details' },
    { key: 'carousel', label: 'Carousel', description: 'Sliding cards' },
    { key: 'featured', label: 'Featured', description: 'Highlight top services' },
  ],
  testimonials: [
    { key: 'cards', label: 'Cards', description: 'Grid of testimonial cards' },
    { key: 'slider', label: 'Slider', description: 'One at a time carousel' },
    { key: 'masonry', label: 'Masonry', description: 'Pinterest-style layout' },
  ],
  team: [
    { key: 'grid', label: 'Grid', description: 'Photo grid with hover details' },
    { key: 'cards', label: 'Cards', description: 'Detailed member cards' },
    { key: 'minimal', label: 'Minimal', description: 'Clean, simple list' },
  ],
  gallery: [
    { key: 'grid', label: 'Grid', description: 'Responsive image grid' },
    { key: 'masonry', label: 'Masonry', description: 'Pinterest-style layout' },
    { key: 'carousel', label: 'Carousel', description: 'Sliding gallery' },
    { key: 'lightbox', label: 'Lightbox', description: 'Click to expand' },
  ],
  faq: [
    { key: 'accordion', label: 'Accordion', description: 'Expandable questions' },
    { key: 'tabs', label: 'Tabs', description: 'Categorized with tabs' },
    { key: 'grid', label: 'Grid', description: 'Cards layout' },
  ],
  pricing: [
    { key: 'cards', label: 'Cards', description: 'Side by side pricing cards' },
    { key: 'table', label: 'Table', description: 'Comparison table' },
    { key: 'minimal', label: 'Minimal', description: 'Simple list view' },
  ],
  stats: [
    { key: 'counters', label: 'Counters', description: 'Animated number counters' },
    { key: 'cards', label: 'Cards', description: 'Stats in cards' },
    { key: 'inline', label: 'Inline', description: 'Horizontal stats bar' },
  ],
  // Default for sections without specific layouts
  default: [
    { key: 'default', label: 'Default', description: 'Standard layout' },
  ],
};

// All available sections by website type
const AVAILABLE_SECTIONS = {
  business: [
    { key: 'home', label: 'Home', required: true },
    { key: 'about', label: 'About Us' },
    { key: 'services', label: 'Services' },
    { key: 'how_it_works', label: 'How It Works' },
    { key: 'why_choose_us', label: 'Why Choose Us' },
    { key: 'stats', label: 'Statistics' },
    { key: 'legacy', label: 'Our Legacy' },
    { key: 'gallery', label: 'Gallery' },
    { key: 'testimonials', label: 'Testimonials' },
    { key: 'faq', label: 'FAQ' },
    { key: 'team', label: 'Our Team' },
    { key: 'clients', label: 'Our Clients' },
    { key: 'pricing', label: 'Pricing' },
    { key: 'locations', label: 'Locations' },
    { key: 'blogs', label: 'Blog' },
  ],
  portfolio: [
    { key: 'home', label: 'Home', required: true },
    { key: 'profile', label: 'Profile' },
    { key: 'education', label: 'Education' },
    { key: 'career', label: 'Career' },
    { key: 'skills', label: 'Skills' },
    { key: 'awards', label: 'Awards' },
    { key: 'testimonials', label: 'Testimonials' },
    { key: 'blogs', label: 'Blog' },
  ],
  freelancer: [
    { key: 'home', label: 'Home', required: true },
    { key: 'profile', label: 'Profile' },
    { key: 'services', label: 'Services' },
    { key: 'how_it_works', label: 'How It Works' },
    { key: 'skills', label: 'Skills' },
    { key: 'testimonials', label: 'Testimonials' },
    { key: 'clients', label: 'Clients' },
    { key: 'pricing', label: 'Pricing' },
    { key: 'faq', label: 'FAQ' },
    { key: 'blogs', label: 'Blog' },
  ],
};

const theme = {
  bgPrimary: '#0f172a',
  bgSecondary: '#1e293b',
  bgTertiary: '#334155',
  border: '#334155',
  textPrimary: '#f8fafc',
  textSecondary: '#94a3b8',
  accent: '#6366f1',
  accentMuted: 'rgba(99, 102, 241, 0.15)',
  success: '#22c55e',
  error: '#ef4444',
};

const SectionManager = ({ websiteType = 'business' }) => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [showAddSection, setShowAddSection] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch current section config
  useEffect(() => {
    fetchSectionConfig();
  }, []);

  const fetchSectionConfig = async () => {
    try {
      const res = await api.get('/business/section-config/');
      if (res.data?.config?.sections?.length > 0) {
        setSections(res.data.config.sections);
      } else {
        // Initialize with defaults from available sections
        const defaults = AVAILABLE_SECTIONS[websiteType]?.map((sec, index) => ({
          key: sec.key,
          label: sec.label,
          visible: true,
          order: index,
          display: index === 0 ? 'page' : 'inline', // First section as page, rest inline
          layout: 'default',
        })) || [];
        setSections(defaults);
      }
    } catch (err) {
      console.error('Error fetching section config:', err);
      // Initialize with defaults
      const defaults = AVAILABLE_SECTIONS[websiteType]?.map((sec, index) => ({
        key: sec.key,
        label: sec.label,
        visible: true,
        order: index,
        display: index === 0 ? 'page' : 'inline',
        layout: 'default',
      })) || [];
      setSections(defaults);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put('/business/section-config/', {
        config: { sections }
      });
      setHasChanges(false);
    } catch (err) {
      console.error('Error saving section config:', err);
    } finally {
      setSaving(false);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newSections = [...sections];
    const draggedItem = newSections[draggedIndex];
    newSections.splice(draggedIndex, 1);
    newSections.splice(index, 0, draggedItem);

    // Update order values
    newSections.forEach((sec, i) => {
      sec.order = i;
    });

    setSections(newSections);
    setDraggedIndex(index);
    setHasChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Section controls
  const toggleVisibility = (index) => {
    const newSections = [...sections];
    // Don't allow hiding required sections (like home)
    const sectionInfo = AVAILABLE_SECTIONS[websiteType]?.find(s => s.key === newSections[index].key);
    if (sectionInfo?.required) return;

    newSections[index].visible = !newSections[index].visible;
    setSections(newSections);
    setHasChanges(true);
  };

  const updateDisplayMode = (index, mode) => {
    const newSections = [...sections];
    newSections[index].display = mode;
    setSections(newSections);
    setHasChanges(true);
  };

  const updateLayout = (index, layout) => {
    const newSections = [...sections];
    newSections[index].layout = layout;
    setSections(newSections);
    setHasChanges(true);
  };

  const removeSection = (index) => {
    const sectionInfo = AVAILABLE_SECTIONS[websiteType]?.find(s => s.key === sections[index].key);
    if (sectionInfo?.required) return;

    const newSections = sections.filter((_, i) => i !== index);
    newSections.forEach((sec, i) => {
      sec.order = i;
    });
    setSections(newSections);
    setHasChanges(true);
  };

  const addSection = (sectionKey) => {
    const sectionInfo = AVAILABLE_SECTIONS[websiteType]?.find(s => s.key === sectionKey);
    if (!sectionInfo) return;
    if (sections.find(s => s.key === sectionKey)) return; // Already exists

    const newSection = {
      key: sectionKey,
      label: sectionInfo.label,
      visible: true,
      order: sections.length,
      display: 'inline',
      layout: 'default',
    };

    setSections([...sections, newSection]);
    setShowAddSection(false);
    setHasChanges(true);
  };

  const resetToDefaults = () => {
    const defaults = AVAILABLE_SECTIONS[websiteType]?.map((sec, index) => ({
      key: sec.key,
      label: sec.label,
      visible: true,
      order: index,
      display: index === 0 ? 'page' : 'inline',
      layout: 'default',
    })) || [];
    setSections(defaults);
    setHasChanges(true);
  };

  // Get available sections that haven't been added yet
  const availableSectionsToAdd = AVAILABLE_SECTIONS[websiteType]?.filter(
    sec => !sections.find(s => s.key === sec.key)
  ) || [];

  const getLayouts = (sectionKey) => {
    return SECTION_LAYOUTS[sectionKey] || SECTION_LAYOUTS.default;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin" size={24} style={{ color: theme.accent }} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold" style={{ color: theme.textPrimary }}>
            Section Manager
          </h3>
          <p className="text-sm mt-1" style={{ color: theme.textSecondary }}>
            Drag to reorder, configure display mode and layouts
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={resetToDefaults}
            className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5"
            style={{ color: theme.textSecondary, backgroundColor: theme.bgTertiary }}
          >
            <RotateCcw size={14} />
            Reset
          </button>
          <button
            onClick={saveConfig}
            disabled={saving || !hasChanges}
            className="px-4 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 disabled:opacity-50"
            style={{
              backgroundColor: hasChanges ? theme.accent : theme.bgTertiary,
              color: '#fff',
            }}
          >
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
            Save Changes
          </button>
        </div>
      </div>

      {/* Section List */}
      <div className="space-y-2">
        {sections.map((section, index) => {
          const Icon = SECTION_ICONS[section.key] || Layers;
          const isExpanded = expandedSection === section.key;
          const layouts = getLayouts(section.key);
          const sectionInfo = AVAILABLE_SECTIONS[websiteType]?.find(s => s.key === section.key);
          const isRequired = sectionInfo?.required;

          return (
            <div
              key={section.key}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className="rounded-lg overflow-hidden transition-all"
              style={{
                backgroundColor: theme.bgSecondary,
                border: `1px solid ${draggedIndex === index ? theme.accent : theme.border}`,
                opacity: draggedIndex === index ? 0.8 : 1,
              }}
            >
              {/* Main Row */}
              <div className="flex items-center gap-3 p-3">
                {/* Drag Handle */}
                <div
                  className="cursor-grab active:cursor-grabbing p-1 rounded"
                  style={{ color: theme.textSecondary }}
                >
                  <GripVertical size={18} />
                </div>

                {/* Icon */}
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: theme.accentMuted }}
                >
                  <Icon size={16} style={{ color: theme.accent }} />
                </div>

                {/* Label & Status */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className="font-medium truncate"
                      style={{ color: section.visible ? theme.textPrimary : theme.textSecondary }}
                    >
                      {section.label || section.key}
                    </span>
                    {isRequired && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                        style={{ backgroundColor: theme.accentMuted, color: theme.accent }}
                      >
                        Required
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span
                      className="text-xs px-1.5 py-0.5 rounded"
                      style={{
                        backgroundColor: section.display === 'page' ? `${theme.success}20` : `${theme.accent}20`,
                        color: section.display === 'page' ? theme.success : theme.accent,
                      }}
                    >
                      {section.display === 'page' ? 'Page' : 'Inline'}
                    </span>
                    <span className="text-xs" style={{ color: theme.textTertiary }}>
                      Layout: {layouts.find(l => l.key === section.layout)?.label || 'Default'}
                    </span>
                  </div>
                </div>

                {/* Controls */}
                <div className="flex items-center gap-1">
                  {/* Visibility Toggle */}
                  <button
                    onClick={() => toggleVisibility(index)}
                    disabled={isRequired}
                    className="p-2 rounded-lg transition-colors disabled:opacity-30"
                    style={{
                      color: section.visible ? theme.success : theme.textSecondary,
                      backgroundColor: section.visible ? `${theme.success}15` : 'transparent',
                    }}
                    title={section.visible ? 'Hide section' : 'Show section'}
                  >
                    {section.visible ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>

                  {/* Expand/Collapse */}
                  <button
                    onClick={() => setExpandedSection(isExpanded ? null : section.key)}
                    className="p-2 rounded-lg transition-colors"
                    style={{ color: theme.textSecondary }}
                  >
                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>

                  {/* Remove */}
                  {!isRequired && (
                    <button
                      onClick={() => removeSection(index)}
                      className="p-2 rounded-lg transition-colors hover:bg-red-500/10"
                      style={{ color: theme.error }}
                      title="Remove section"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>

              {/* Expanded Options */}
              {isExpanded && (
                <div
                  className="px-4 pb-4 pt-2 space-y-4"
                  style={{ borderTop: `1px solid ${theme.border}` }}
                >
                  {/* Display Mode */}
                  <div>
                    <label className="text-sm font-medium mb-2 block" style={{ color: theme.textSecondary }}>
                      Display Mode
                    </label>
                    <div className="flex gap-2">
                      {['page', 'inline'].map((mode) => (
                        <button
                          key={mode}
                          onClick={() => updateDisplayMode(index, mode)}
                          className="flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                          style={{
                            backgroundColor: section.display === mode ? theme.accent : theme.bgTertiary,
                            color: section.display === mode ? '#fff' : theme.textSecondary,
                          }}
                        >
                          {mode === 'page' ? 'Separate Page' : 'Inline Section'}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs mt-1.5" style={{ color: theme.textTertiary }}>
                      {section.display === 'page'
                        ? 'Shows as a separate page with its own navigation link'
                        : 'Appears inline with other sections on the same scroll'
                      }
                    </p>
                  </div>

                  {/* Layout Variant */}
                  <div>
                    <label className="text-sm font-medium mb-2 block" style={{ color: theme.textSecondary }}>
                      Layout Style
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {layouts.map((layout) => (
                        <button
                          key={layout.key}
                          onClick={() => updateLayout(index, layout.key)}
                          className="p-3 rounded-lg text-left transition-colors"
                          style={{
                            backgroundColor: section.layout === layout.key ? theme.accentMuted : theme.bgTertiary,
                            border: `1px solid ${section.layout === layout.key ? theme.accent : 'transparent'}`,
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <Layout size={14} style={{ color: section.layout === layout.key ? theme.accent : theme.textSecondary }} />
                            <span
                              className="text-sm font-medium"
                              style={{ color: section.layout === layout.key ? theme.accent : theme.textPrimary }}
                            >
                              {layout.label}
                            </span>
                          </div>
                          <p className="text-xs mt-1" style={{ color: theme.textTertiary }}>
                            {layout.description}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add Section Button */}
      {availableSectionsToAdd.length > 0 && (
        <div className="relative">
          <button
            onClick={() => setShowAddSection(!showAddSection)}
            className="w-full py-3 rounded-lg border-2 border-dashed transition-colors flex items-center justify-center gap-2"
            style={{
              borderColor: showAddSection ? theme.accent : theme.border,
              color: showAddSection ? theme.accent : theme.textSecondary,
              backgroundColor: showAddSection ? theme.accentMuted : 'transparent',
            }}
          >
            <Plus size={18} />
            Add Section
          </button>

          {/* Dropdown */}
          {showAddSection && (
            <div
              className="absolute top-full left-0 right-0 mt-2 rounded-lg overflow-hidden z-10 max-h-64 overflow-y-auto"
              style={{ backgroundColor: theme.bgSecondary, border: `1px solid ${theme.border}` }}
            >
              {availableSectionsToAdd.map((sec) => {
                const Icon = SECTION_ICONS[sec.key] || Layers;
                return (
                  <button
                    key={sec.key}
                    onClick={() => addSection(sec.key)}
                    className="w-full flex items-center gap-3 px-4 py-3 transition-colors hover:bg-white/5 text-left"
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: theme.accentMuted }}
                    >
                      <Icon size={16} style={{ color: theme.accent }} />
                    </div>
                    <span style={{ color: theme.textPrimary }}>{sec.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Info */}
      <div
        className="p-4 rounded-lg mt-6"
        style={{ backgroundColor: theme.accentMuted }}
      >
        <h4 className="font-medium mb-2" style={{ color: theme.accent }}>
          How it works
        </h4>
        <ul className="space-y-1.5 text-sm" style={{ color: theme.textSecondary }}>
          <li>• <strong>Page</strong> sections get their own navigation link and route</li>
          <li>• <strong>Inline</strong> sections appear together on a scrollable page</li>
          <li>• Drag sections to change their order</li>
          <li>• Choose different layouts to change how content is displayed</li>
        </ul>
      </div>
    </div>
  );
};

export default SectionManager;
